"use client"

import { useEffect, useState, useRef, useMemo } from "react"
import { FormGroup, Label, Input, Button, Row, Col, Card, CardBody, CardHeader, Badge } from "reactstrap"
import { Edit2, Save, X, MapPin, Clock } from "react-feather"
import "./MerchantInfo.css"
import WorkingHoursForm from "./WorkingHoursForm"
import LocationPicker from "./LocationPicker"
import Select from "react-select"
import { updateMerchantDetails } from "../../../../api-handle/merchantApi"
import { getTypes, getCategoriesByType, getSubCategories } from "../../../../api-handle/utilApi"
import { toast } from "react-toastify"
import { Building } from "lucide-react"
import { removeFile, uploadSingleFile } from "../../../../api-handle/fileApi"
import { refreshMenuItems } from "../../../../Layout/Sidebar/Menu"
import { sanitizeArabicEvent, sanitizeArabicFieldEvent } from "../../../../Services/arabicInput"
import { LoadScript } from "@react-google-maps/api"

const ALL_GENDER_VALUES = ["male", "female", "boys", "girls"]

// A category's gender tags drive the default selection. Empty (genderless)
// or full coverage collapses to the "all" shorthand the selector already uses.
const genderFromCategory = (cat) => {
  const genders = (Array.isArray(cat?.gender) ? cat.gender : []).filter((g) =>
    ALL_GENDER_VALUES.includes(g)
  )
  if (!genders.length || genders.length === ALL_GENDER_VALUES.length) return ["all"]
  return genders
}

// Restrict the gender selector to what the chosen category supports. A
// genderless category (empty) or one tagged with every gender keeps the full
// list (including the "All" shorthand); a restricted category drops "All" and
// only offers its own genders.
const genderOptionsFromCategory = (cat, options) => {
  const genders = (Array.isArray(cat?.gender) ? cat.gender : []).filter((g) =>
    ALL_GENDER_VALUES.includes(g)
  )
  if (!genders.length || genders.length === ALL_GENDER_VALUES.length) return options
  return options.filter((opt) => genders.includes(opt.value))
}

// Gym/academy categories are genderless; their gender comes from the chosen
// SUB-CATEGORY instead. These mirror the category helpers above but read the
// sub-category's gender array.
const genderFromSubCategory = (sub) => genderFromCategory(sub)
const genderOptionsFromSubCategory = (sub, options) => genderOptionsFromCategory(sub, options)

const MerchantInfo = ({ merchantData, setRefetch }) => {
  // Types come from the API (key/title/sortOrder/isActive) and are the single
  // source of truth for the selectable list, labels and ordering.
  const [types, setTypes] = useState([])
  // Categories/sub-categories keyed by type key, so nothing branches on the
  // literal "gym"/"academy"/"marketplace" strings.
  const [categoriesByType, setCategoriesByType] = useState({})
  const [subCategoriesByType, setSubCategoriesByType] = useState({})
  const loadedCategoryTypes = useRef(new Set())

  const typeIdByKey = useMemo(() => {
    const map = {}
    types.forEach((t) => { map[t.key] = t._id })
    return map
  }, [types])
  const typeMetaByKey = useMemo(() => {
    const map = {}
    types.forEach((t) => { map[t.key] = t })
    return map
  }, [types])
  const getTypeLabel = (key) =>
    typeMetaByKey[key]?.title || (key ? key.charAt(0).toUpperCase() + key.slice(1) : "")
  const genderOptions = [
    { value: "all", label: "All" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "boys", label: "Boys" },
    { value: "girls", label: "Girls" }
  ]

  // Gender options for a type's selector. Marketplace narrows by its selected
  // category; gym/academy narrow by their selected SUB-CATEGORY (their
  // categories are genderless). Falls back to the full list until the relevant
  // source is picked.
  const genderOptionsForType = (key) => {
    if (key === "marketplace") {
      const cat = (categoriesByType[key] || []).find(
        (c) => c._id === updatedData.marketplaceCategory
      )
      return cat ? genderOptionsFromCategory(cat, genderOptions) : genderOptions
    }
    const sub = (subCategoriesByType[key] || []).find(
      (s) => s._id === updatedData[key]?.subCategory
    )
    return sub ? genderOptionsFromSubCategory(sub, genderOptions) : genderOptions
  }

  // Step 1: only fetch the list of types up-front, building the key -> typeId map.
  const fetchTypes = async () => {
    try {
      const typesResponse = await getTypes()
      setTypes(typesResponse?.types || [])
    } catch (error) {
      console.error("Error fetching types:", error)
      toast.error("Failed to fetch types")
    }
  }

  // Step 2: load a single type's categories on demand (only once per type).
  const loadCategoriesForType = async (key) => {
    const typeId = typeIdByKey[key]
    if (!typeId || loadedCategoryTypes.current.has(key)) return
    loadedCategoryTypes.current.add(key)
    try {
      const response = await getCategoriesByType(typeId)
      const categories = response?.categories || []
      setCategoriesByType((prev) => ({ ...prev, [key]: categories }))
      
      // Auto-select first category for gym/academy if not already set
      if ((key === "gym" || key === "academy") && categories.length > 0) {
        const currentCategory = updatedData[key]?.category
        if (!currentCategory) {
          const firstCategoryId = categories[0]._id
          setUpdatedData((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              category: firstCategoryId,
            },
          }))
          // Load subcategories for the first category - this will auto-select first subcategory and gender
          loadSubCategories(firstCategoryId, key)
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error)
      toast.error("Failed to fetch categories")
      loadedCategoryTypes.current.delete(key)
    }
  }

  const loadSubCategories = async (categoryId, target) => {
    if (!categoryId) {
      setSubCategoriesByType((prev) => ({ ...prev, [target]: [] }))
      return
    }
    try {
      const response = await getSubCategories(categoryId)
      const subcategories = response?.filteredData?.categories || []
      setSubCategoriesByType((prev) => ({ ...prev, [target]: subcategories }))
      
      // Auto-select first subcategory for gym/academy if not already set
      if ((target === "gym" || target === "academy") && subcategories.length > 0) {
        const currentSubCategory = updatedData[target]?.subCategory
        if (!currentSubCategory) {
          const firstSubCategory = subcategories[0]
          const firstSubCategoryId = firstSubCategory._id
          const genderType = firstSubCategory?.gender || []
          
          setUpdatedData((prev) => ({
            ...prev,
            [target]: {
              ...prev[target],
              subCategory: firstSubCategoryId,
              // Apply gender from first subcategory directly
              genderType: genderType,
            },
          }))
        }
      }
    } catch (error) {
      console.error("Error fetching sub-categories:", error)
      setSubCategoriesByType((prev) => ({ ...prev, [target]: [] }))
    }
  }

  // On mount: fetch only the types.
  useEffect(() => {
    fetchTypes()
  }, [])

  // Function to build initial data from merchantData. The backend returns
  // `type` as populated Type refs ({ _id, key, title }); we work with the keys
  // internally because the detail objects are named `${key}Detail`.
  const buildInitialData = (merchantData) => {
    const selectedTypes = (merchantData?.type || [])
      .map((t) => (t && typeof t === "object" ? t.key : t))
      .filter(Boolean)

    const initialData = {
      type: selectedTypes,
      marketplaceName: merchantData?.marketplaceName || "",
      marketplaceNameAr: merchantData?.marketplaceNameAr || "",
      marketplaceCategory: merchantData?.marketplaceCategory?._id || merchantData?.marketplaceCategory || "",
      marketplaceGenderType: merchantData?.marketplaceGenderType || [],
      logo: merchantData?.logo || "",
      ...selectedTypes.reduce((acc, type) => {
        const detailData = merchantData?.[`${type}Detail`] || {}
        // Use location from detail if available, otherwise use merchant-level location
        const location = detailData.location || merchantData?.location || { latitude: "", longitude: "", address: "" }
        
        acc[type] = {
          name: detailData.name || "",
          nameAr: detailData.nameAr || "",
          description: detailData.description || "",
          descriptionAr: detailData.descriptionAr || "",
          // Any non-marketplace type can carry a category/sub-category.
          ...(type !== "marketplace" ? {
            category: detailData.category?._id || detailData.category || "",
            subCategory: detailData.subCategory?._id || detailData.subCategory || "",
          } : {}),
          location: location,
          workingHours: detailData.workingHours || [],
          images: detailData.images || [],
          coverImage: detailData.coverImage || "",
          ...(type === "marketplace" ? {} : { genderType: detailData.genderType || [] })
        }
        return acc
      }, {}),
    }
    return initialData
  }

  const [editMode, setEditMode] = useState(false)
  const [updatedData, setUpdatedData] = useState(() => buildInitialData(merchantData))

  // Sync updatedData when merchantData changes
  useEffect(() => {
    if (merchantData) {
      const newInitialData = buildInitialData(merchantData)
      // Only update if not in edit mode to avoid losing user's edits
      if (!editMode) {
        setUpdatedData(newInitialData)
      }
    }
  }, [merchantData, editMode])

  useEffect(() => {
    console.log("Updated Data:", updatedData)
  }, [updatedData])

  useEffect(() => {
    const gymCategoryId = merchantData?.gymDetail?.category?._id || merchantData?.gymDetail?.category
    const academyCategoryId = merchantData?.academyDetail?.category?._id || merchantData?.academyDetail?.category
    const marketplaceCategoryId = merchantData?.marketplaceCategory?._id || merchantData?.marketplaceCategory
    if (gymCategoryId) loadSubCategories(gymCategoryId, "gym")
    if (academyCategoryId) loadSubCategories(academyCategoryId, "academy")
    if (marketplaceCategoryId) loadSubCategories(marketplaceCategoryId, "marketplace")
  }, [merchantData])

  // Cascade: load categories for each selected type once the types are available.
  useEffect(() => {
    if (!Object.keys(typeIdByKey).length) return
    ;(updatedData?.type || []).forEach((key) => loadCategoriesForType(key))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedData.type, typeIdByKey])

  const handleChange = (e, type) => {
    const { name, value } = e.target
    setUpdatedData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        // Arabic fields (nameAr, descriptionAr) only accept Arabic text
        [name]: sanitizeArabicFieldEvent(e),
      },
    }))
  }

  const handleLocationChange = (locationData, type) => {
    setUpdatedData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        location: locationData,
      },
    }))
  }

  const handleTypeChange = (e) => {
    const { value, checked } = e.target
    setUpdatedData((prev) => {
      const newTypes = checked ? [...prev.type, value] : prev.type.filter((t) => t !== value)

      const newData = { ...prev, type: newTypes }

      if (checked && !prev[value]) {
        newData[value] = {
          name: "",
          nameAr: "",
          description: "",
          descriptionAr: "",
          ...(value !== "marketplace" ? { category: "", subCategory: "" } : {}),
          location: { latitude: "", longitude: "", address: "" },
          workingHours: [],
          ...(value === "marketplace" ? {} : { genderType: [] })
        }
      }

      return newData
    })
  }

  const handleCategoryChange = (e, type) => {
    const { value } = e.target
    setUpdatedData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        category: value,
        subCategory: "",
        // Gym/academy categories are genderless — gender now comes from the
        // sub-category, so changing the category clears the gender until a
        // sub-category is picked.
        genderType: [],
      },
    }))
    loadSubCategories(value, type)
  }

  const handleSubCategoryChange = (e, type) => {
    const { value } = e.target
    const selectedSub = (subCategoriesByType[type] || []).find((s) => s._id === value)
    setUpdatedData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        subCategory: value,
        // Picking a sub-category resets gender to its default; the vendor can
        // still adjust it in the gender selector afterwards.
        genderType: selectedSub ? genderFromSubCategory(selectedSub) : [],
      },
    }))
  }

  const handleGenderTypeChange = (selectedOptions, type) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : []
    setUpdatedData((prev) => {
      const previous = prev[type]?.genderType || []
      const hadAll = previous.includes("all")
      const hasAll = selectedValues.includes("all")
      let nextValues = selectedValues
      if (hasAll && !hadAll) {
        nextValues = ["all"]
      } else if (hasAll && hadAll && selectedValues.length > 1) {
        nextValues = selectedValues.filter((v) => v !== "all")
      }
      return {
        ...prev,
        [type]: {
          ...prev[type],
          genderType: nextValues,
        },
      }
    })
  }

  // Marketplace category/sub-category/gender are account-level fields stored at
  // the top level of updatedData (not nested under a type), mirroring registration.
  const handleMarketplaceCategoryChange = (e) => {
    const { value } = e.target
    const selectedCat = (categoriesByType.marketplace || []).find((c) => c._id === value)
    setUpdatedData((prev) => ({
      ...prev,
      marketplaceCategory: value,
      ...(selectedCat ? { marketplaceGenderType: genderFromCategory(selectedCat) } : {}),
    }))
    loadSubCategories(value, "marketplace")
  }

  const handleMarketplaceGenderTypeChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : []
    setUpdatedData((prev) => {
      const previous = prev.marketplaceGenderType || []
      const hadAll = previous.includes("all")
      const hasAll = selectedValues.includes("all")
      let nextValues = selectedValues
      if (hasAll && !hadAll) {
        nextValues = ["all"]
      } else if (hasAll && hadAll && selectedValues.length > 1) {
        nextValues = selectedValues.filter((v) => v !== "all")
      }
      return { ...prev, marketplaceGenderType: nextValues }
    })
  }

  const handleSave = async () => {
    if (updatedData.type.length === 0) {
      toast.warn("Please select at least one business type.")
      return
    }

    // Validate for empty fields with specific messages
    for (const type of updatedData.type) {
      const details = updatedData[type]
      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1)

      if (type === "marketplace") {
        if (!updatedData.marketplaceName || !updatedData.marketplaceNameAr) {
          toast.warn("Please fill in marketplace name (English & Arabic).")
          return
        }
        if (!updatedData.marketplaceCategory) {
          toast.warn("Please select a Category for Marketplace.")
          return
        }
        continue
      }
      if (!details.name || !details.nameAr) {
        toast.warn(`Please fill in ${typeLabel} business name (English & Arabic).`)
        return
      }
      if (!details.description || !details.descriptionAr) {
        toast.warn(`Please fill in ${typeLabel} description (English & Arabic).`)
        return
      }
      // Require a category for any non-marketplace type that actually has
      // categories defined for it.
      if ((categoriesByType[type] || []).length > 0 && !details.category) {
        toast.warn(`Please select a Category for ${typeLabel}.`)
        return
      }
      if (!details?.genderType || details.genderType.length === 0) {
        toast.warn(`Please select at least one Gender Type for ${typeLabel}.`)
        return
      }
      if (
        !details.location ||
        !details.location.latitude ||
        !details.location.longitude ||
        !details.location.address
      ) {
        toast.warn(`Please set the ${typeLabel} location (address with coordinates).`)
        return
      }
    }

    try {
      // Build detail objects for each type - include all fields to ensure complete data
      const detailPayload = updatedData.type.reduce((acc, type) => {
        if (type !== "marketplace") {
          const detail = updatedData[type] || {}
          // Include all fields from the detail object
          // For arrays and objects, use the actual values (backend will handle merging)
          acc[`${type}Detail`] = {
            name: detail.name || "",
            nameAr: detail.nameAr || "",
            description: detail.description || "",
            descriptionAr: detail.descriptionAr || "",
            location: detail.location && typeof detail.location === 'object' 
              ? detail.location 
              : { latitude: "", longitude: "", address: "" },
            workingHours: Array.isArray(detail.workingHours) ? detail.workingHours : [],
            images: Array.isArray(detail.images) ? detail.images : [],
            coverImage: detail.coverImage || "",
            genderType: Array.isArray(detail.genderType) ? detail.genderType : [],
            // Already inside the non-marketplace branch — every such type can
            // carry a category/sub-category.
            category: detail.category || null,
            subCategory: detail.subCategory || null,
          }
        }
        return acc
      }, {})

      // Get merchant-level location from the first non-marketplace type that has location
      // This preserves the merchant-level location that was set during registration
      const merchantLocation = updatedData.type
        .filter(type => type !== "marketplace")
        .map(type => updatedData[type]?.location)
        .find(loc => loc && (loc.latitude || loc.longitude || loc.address)) || null

      const payload = {
        type: updatedData.type,
        ...(updatedData.marketplaceName !== undefined ? { marketplaceName: updatedData.marketplaceName } : {}),
        ...(updatedData.marketplaceNameAr !== undefined ? { marketplaceNameAr: updatedData.marketplaceNameAr } : {}),
        ...(updatedData.logo !== undefined ? { logo: updatedData.logo } : {}),
        ...(merchantLocation ? { location: merchantLocation } : {}),
        // Marketplace account-level config — only sent when marketplace is enabled
        ...(updatedData.type.includes("marketplace")
          ? {
              marketplaceCategory: updatedData.marketplaceCategory || null,
              marketplaceGenderType: Array.isArray(updatedData.marketplaceGenderType)
                ? updatedData.marketplaceGenderType
                : [],
            }
          : {}),
        ...detailPayload,
      }

      console.log("Sending payload to updateMerchantDetails:", JSON.stringify(payload, null, 2))
      await updateMerchantDetails(payload)
      localStorage.setItem("merchant-types", updatedData.type.join(","))
      refreshMenuItems()
      setEditMode(false)
      toast.success("Merchant details updated successfully!")
      // Trigger refetch to get updated data
      if (setRefetch) {
        setRefetch((prev) => !prev)
      }
    } catch (error) {
      console.error("Error updating merchant details:", error)
      toast.error("Failed to update merchant details.")
    }
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case "gym":
        return <Building size={16} />
      case "academy":
        return <Building size={16} />
      case "marketplace":
        return <Building size={16} />
      default:
        return <Building size={16} />
    }
  }
  const libraries = ["places"];

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={libraries}
    >
      <div className="merchant-info-container">
        {/* Header Section */}
        <div className="merchant-header">
          <div className="header-content">
            <h1 className="page-title">Merchant Information</h1>
            <p className="page-subtitle">Manage your business details and settings</p>
          </div>
          <div className="header-actions">
            {editMode ? (
              <>
                <Button color="success" onClick={handleSave} className="action-btn me-2">
                  <Save size={16} className="me-1" /> Save Changes
                </Button>
                <Button color="secondary" onClick={() => { setEditMode(false); setUpdatedData(buildInitialData(merchantData)); }} className="action-btn">
                  <X size={16} className="me-1" /> Cancel
                </Button>
              </>
            ) : (
              <Button color="primary" onClick={() => setEditMode(true)} className="action-btn">
                <Edit2 size={16} className="me-1" /> Edit Details
              </Button>
            )}
          </div>
        </div>

        {/* Business Types Selection Card */}
        <Card className="modern-card mb-4">
          <CardHeader className="card-header-modern">
            <div className="d-flex align-items-center">
              <Building size={20} className="me-2 text-primary" />
              <h5 className="card-title-modern mb-0">Business Types</h5>
            </div>
          </CardHeader>
          <CardBody>
            <FormGroup>
              <Label className="form-label-modern">Select Business Types</Label>
              <Row className="business-types-row">
                {types.map((t) => (
                  <Col md={4} key={t.key} className="mb-3">
                    <div className="custom-checkbox-wrapper">
                      <label className="custom-checkbox-label">
                        <input
                          type="checkbox"
                          value={t.key}
                          checked={updatedData.type.includes(t.key)}
                          onChange={handleTypeChange}
                          disabled={!editMode}
                          className="custom-checkbox-input"
                        />
                        <span className="custom-checkbox-checkmark"></span>
                        <span className="d-flex align-items-center">
                          {getTypeIcon(t.key)}
                          <span className="ms-2">{t.title}</span>
                        </span>
                      </label>
                    </div>
                  </Col>
                ))}
              </Row>
            </FormGroup>

            {updatedData.type.length > 0 && (
              <div className="selected-types-section">
                <hr className="section-divider" />
                <p className="selected-types-label">Selected business types:</p>
                <div className="selected-types-badges">
                  {updatedData.type.map((type) => (
                    <Badge key={type} color="primary" className="type-badge me-2 mb-2">
                      <span className="ms-1">{getTypeLabel(type)}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Business Details Cards */}
        {updatedData.type
          .filter((type) => type !== "marketplace")
          .map((type, index) => (
            <Card key={index} className="modern-card mb-4">
              <CardHeader className="card-header-modern">
                <div className="d-flex align-items-center">
                  {getTypeIcon(type)}
                  <h5 className="card-title-modern mb-0 ms-2">{getTypeLabel(type)} Details</h5>
                </div>
              </CardHeader>
              <CardBody>
                {/* Basic Information Section */}
                <div className="form-section">
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label-modern">
                          Business Name <span className="required-asterisk">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="name"
                          value={updatedData[type]?.name || ""}
                          onChange={(e) => handleChange(e, type)}
                          disabled={!editMode}
                          className="modern-input"
                          placeholder="Enter business name"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label-modern">
                          Business Name (Arabic) <span className="required-asterisk">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="nameAr"
                          value={updatedData[type]?.nameAr || ""}
                          onChange={(e) => handleChange(e, type)}
                          disabled={!editMode}
                          className="modern-input rtl-input"
                          placeholder="أدخل اسم العمل"
                          dir="rtl"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label-modern">
                          Description <span className="required-asterisk">*</span>
                        </Label>
                        <Input
                          type="textarea"
                          name="description"
                          value={updatedData[type]?.description || ""}
                          onChange={(e) => handleChange(e, type)}
                          disabled={!editMode}
                          className="modern-textarea"
                          placeholder="Enter business description"
                          rows={3}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label-modern">
                          Description (Arabic) <span className="required-asterisk">*</span>
                        </Label>
                        <Input
                          type="textarea"
                          name="descriptionAr"
                          value={updatedData[type]?.descriptionAr || ""}
                          onChange={(e) => handleChange(e, type)}
                          disabled={!editMode}
                          className="modern-textarea rtl-input"
                          placeholder="أدخل وصف العمل"
                          dir="rtl"
                          rows={3}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {type !== "marketplace" && (categoriesByType[type] || []).length > 0 && (
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="form-label-modern">
                            Category <span className="required-asterisk">*</span>
                          </Label>
                          <Input
                            type="select"
                            name="category"
                            value={updatedData[type]?.category || ""}
                            onChange={(e) => handleCategoryChange(e, type)}
                            disabled={!editMode}
                            className="modern-select"
                          >
                            <option value="">Select Category</option>
                            {(categoriesByType[type] || []).map((cat) => (
                              <option key={cat._id} value={cat._id}>
                                {cat.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="form-label-modern">Sub-Category</Label>
                          <Input
                            type="select"
                            name="subCategory"
                            value={updatedData[type]?.subCategory || ""}
                            onChange={(e) => handleSubCategoryChange(e, type)}
                            disabled={!editMode || !updatedData[type]?.category}
                            className="modern-select"
                          >
                            <option value="">
                              {updatedData[type]?.category
                                ? "Select Sub-Category"
                                : "Select category first"}
                            </option>
                            {(subCategoriesByType[type] || []).map((sub) => (
                              <option key={sub._id} value={sub._id}>
                                {sub.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  {/* Gender Type Multi-Select */}
                  {type !== "marketplace" && (
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="form-label-modern">
                            Gender Types <span className="required-asterisk">*</span>
                          </Label>
                          <Select
                            id={`genderType-${type}`}
                            isMulti
                            options={genderOptionsForType(type)}
                            value={genderOptions.filter(option => updatedData[type]?.genderType?.includes(option.value))}
                            onChange={(selectedOptions) => handleGenderTypeChange(selectedOptions, type)}
                            isDisabled={!editMode || !updatedData[type]?.subCategory}
                            className="mb-3"
                            placeholder={
                              updatedData[type]?.subCategory
                                ? "Select gender types..."
                                : "Select sub-category first"
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                </div>

                <hr className="section-divider" />

                {/* Location Section with Smart Picker */}
                <div className="form-section">
                  <div className="section-header">
                    <MapPin size={18} className="me-2 text-primary" />
                    <h6 className="section-title">Location Information</h6>
                  </div>
                  <LocationPicker
                    location={updatedData[type]?.location || { latitude: "", longitude: "", address: "" }}
                    onLocationChange={(locationData) => handleLocationChange(locationData, type)}
                    disabled={!editMode}
                  />
                </div>

                <hr className="section-divider" />

                {/* Working Hours Section */}
                <div className="form-section">
                  <div className="section-header">
                    <Clock size={18} className="me-2 text-primary" />
                    <h6 className="section-title">Working Hours</h6>
                  </div>
                  <WorkingHoursForm
                    workingHours={updatedData[type]?.workingHours || []}
                    setWorkingHours={(updatedHours) =>
                      setUpdatedData((prev) => ({
                        ...prev,
                        [type]: { ...prev[type], workingHours: updatedHours },
                      }))
                    }
                    editMode={editMode}
                  />
                </div>
              </CardBody>
            </Card>
          ))}

        {/* Marketplace Details Card */}
        {updatedData.type.includes("marketplace") && (
          <Card className="modern-card mb-4">
            <CardHeader className="card-header-modern">
              <div className="d-flex align-items-center">
                <Building size={16} />
                <h5 className="card-title-modern mb-0 ms-2">Marketplace Details</h5>
              </div>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label-modern">
                      Marketplace Name <span className="required-asterisk">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="marketplaceName"
                      value={updatedData.marketplaceName || ""}
                      onChange={(e) =>
                        setUpdatedData((prev) => ({
                          ...prev,
                          marketplaceName: e.target.value,
                        }))
                      }
                      disabled={!editMode}
                      className="modern-input"
                      placeholder="Enter Marketplace Name"
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label-modern">
                      Marketplace Name (Arabic) <span className="required-asterisk">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="marketplaceNameAr"
                      value={updatedData.marketplaceNameAr || ""}
                      onChange={(e) =>
                        setUpdatedData((prev) => ({
                          ...prev,
                          marketplaceNameAr: sanitizeArabicEvent(e),
                        }))
                      }
                      disabled={!editMode}
                      className="modern-input rtl-input"
                      placeholder="أدخل اسم السوق"
                      dir="rtl"
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Marketplace Category and Gender Types in same row */}
              <Row className="mt-3">
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label-modern">
                      Category <span className="required-asterisk">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="marketplaceCategory"
                      value={updatedData.marketplaceCategory || ""}
                      onChange={handleMarketplaceCategoryChange}
                      disabled={!editMode}
                      className="modern-select"
                    >
                      <option value="">Select Category</option>
                      {(categoriesByType.marketplace || []).map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label-modern">Gender Types</Label>
                    <Select
                      id="genderType-marketplace"
                      isMulti
                      options={genderOptionsForType("marketplace")}
                      value={genderOptions.filter((option) =>
                        updatedData.marketplaceGenderType?.includes(option.value)
                      )}
                      onChange={handleMarketplaceGenderTypeChange}
                      isDisabled={!editMode || !updatedData.marketplaceCategory}
                      className="mb-3"
                      placeholder={
                        updatedData.marketplaceCategory
                          ? "Select gender types..."
                          : "Select category first"
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Marketplace Logo Upload */}
              <Row className="mt-3">
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label-modern">Marketplace Logo</Label>
                    {editMode && (
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0]
                          if (!file) return
                          try {
                            const response = await uploadSingleFile(file)
                            setUpdatedData((prev) => ({
                              ...prev,
                              logo: response.fileName,
                            }))
                          } catch (err) {
                            toast.error("Failed to upload logo")
                          }
                        }}
                      />
                    )}

                    {/* Logo Preview */}
                    {updatedData.logo && (
                      <div className="mt-2 position-relative" style={{ width: "120px" }}>
                        <img
                          src={`${process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL}/${updatedData.logo}`}
                          alt="Marketplace Logo"
                          style={{
                            width: "100%",
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                            padding: "4px",
                          }}
                        />
                        {editMode && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute"
                            style={{ top: "-10px", right: "-10px" }}
                            onClick={async () => {
                              try {
                                await removeFile(updatedData.logo)
                                setUpdatedData((prev) => ({ ...prev, logo: "" }))
                              } catch (err) {
                                toast.error("Failed to remove logo")
                              }
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Empty State */}
        {updatedData.type.length === 0 && (
          <Card className="modern-card">
            <CardBody className="text-center py-5">
              <div className="empty-state">
                <Building size={48} className="text-muted mb-3" />
                <h5 className="empty-state-title">No Business Types Selected</h5>
                <p className="empty-state-text">
                  Please select at least one business type to configure your merchant details.
                </p>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </LoadScript>
  )
}

export default MerchantInfo